/**
 * useLocale — localStorage-backed locale state hook (D-44).
 *
 * Reads/writes localStorage.getItem("storm-locale"). Returns "en" | "ar" | null.
 * Subscribers are notified on same-tab changes via an internal Set; cross-tab
 * changes are surfaced via the native "storage" event.
 *
 * The locale picker is the only screen that SETS the initial locale; the
 * GlobeLocaleDropdown calls setLocale for subsequent changes.
 * Reinstall / DOM Storage clear → null → LocaleGuard redirects to /picker.
 */
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "storm-locale";

type Locale = "en" | "ar" | null;

function readLocale(): Locale {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === "en" || raw === "ar") return raw;
  return null;
}

const subscribers = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    subscribers.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

function notifySameTab(): void {
  for (const cb of subscribers) cb();
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, readLocale, () => null);
}

export function useSetLocale() {
  return useCallback((next: "en" | "ar") => {
    localStorage.setItem(STORAGE_KEY, next);
    notifySameTab();
  }, []);
}

export function clearLocale(): void {
  localStorage.removeItem(STORAGE_KEY);
  notifySameTab();
}
